import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const WEEK_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const POPOVER_MAX_WIDTH = 720;
const POPOVER_FIXED_HEIGHT = 404;

const pad2 = (value) => String(value).padStart(2, '0');

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const parseIsoDate = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return null;
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

const formatDisplayDate = (value) => {
  const date = parseIsoDate(value);
  if (!date) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatMonthLabel = (date) => date.toLocaleString('en-US', { month: 'long', year: 'numeric' });

const calcNights = (startIso, endIso) => {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end) return 0;
  const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
};

const buildMonthCells = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const dateObj = new Date(year, month, day);
    const iso = toIsoDate(dateObj);
    cells.push({ dateObj, iso, day });
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
};

const CalendarIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="3" y="4.5" width="14" height="12" rx="2" />
    <path d="M6 3.5v3M14 3.5v3M3 8.5h14" />
  </svg>
);

const NavChevron = ({ direction = 'left' }) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    {direction === 'left' ? <path d="M12.5 5l-5 5 5 5" strokeLinecap="round" strokeLinejoin="round" /> : null}
    {direction === 'right' ? <path d="M7.5 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" /> : null}
  </svg>
);

const buildPopoverLayout = (pickerElement, dimensions) => {
  if (!pickerElement) return null;
  const pickerRect = pickerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth || 0;
  const viewportHeight = window.innerHeight || 0;
  const padding = 12;
  const maxHeight = Math.max(0, viewportHeight - padding * 2);
  const width = dimensions?.width ?? Math.max(0, Math.min(POPOVER_MAX_WIDTH, viewportWidth - padding * 2));
  const height = dimensions?.height ?? Math.min(POPOVER_FIXED_HEIGHT, maxHeight || POPOVER_FIXED_HEIGHT);

  let left = pickerRect.left;
  if (left + width > viewportWidth - padding) {
    left = viewportWidth - padding - width;
  }
  if (left < padding) left = padding;

  let top = pickerRect.bottom + 8;
  if (top + height > viewportHeight - padding) {
    const above = pickerRect.top - height - 8;
    top = above >= padding ? above : padding;
  }

  return {
    position: 'fixed',
    left,
    top,
    width,
    height,
    minHeight: height,
    maxHeight,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    zIndex: 1200,
  };
};

const DateRangePicker = ({
  checkIn,
  checkOut,
  onChange,
  minDate,
  days = [],
  loading = false,
  error = '',
  month: controlledMonth,
  onMonthChange,
  mode = 'popover',
  closeOnComplete = true,
  disabled = false,
  minNights = 1,
  showInputs = true,
  className = '',
}) => {
  const todayIso = useMemo(() => toIsoDate(new Date()), []);
  const minIso = minDate || todayIso;
  const [open, setOpen] = useState(mode === 'inline');
  const [localMonth, setLocalMonth] = useState(() => controlledMonth || new Date());
  const [popoverStyle, setPopoverStyle] = useState(null);
  const [panelError, setPanelError] = useState('');
  const pickerRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverDimensionsRef = useRef(null);
  const selectionRef = useRef({ checkIn, checkOut });
  const propSelectionRef = useRef({ checkIn, checkOut });
  const replaceExistingRangeRef = useRef(Boolean(checkIn && checkOut));
  const month = controlledMonth || localMonth;
  const setMonth = useCallback(
    (next) => {
      if (controlledMonth && onMonthChange) {
        onMonthChange(next);
      } else {
        setLocalMonth(next);
      }
    },
    [controlledMonth, onMonthChange]
  );

  useEffect(() => {
    if (mode === 'inline') {
      setOpen(true);
    }
  }, [mode]);

  useEffect(() => {
    selectionRef.current = { checkIn, checkOut };
  }, [checkIn, checkOut]);

  useEffect(() => {
    const previous = propSelectionRef.current;
    const previousHasRange = Boolean(previous.checkIn && previous.checkOut);
    const currentHasRange = Boolean(checkIn && checkOut);
    if (mode === 'popover' && open && !previousHasRange && currentHasRange) {
      setOpen(false);
    }
    propSelectionRef.current = { checkIn, checkOut };
  }, [checkIn, checkOut, mode, open]);

  useEffect(() => {
    if (!open) {
      replaceExistingRangeRef.current = false;
      return;
    }
    replaceExistingRangeRef.current = Boolean(checkIn && checkOut);
  }, [checkIn, checkOut, open]);

  useEffect(() => {
    if (!open || mode !== 'popover') {
      popoverDimensionsRef.current = null;
      if (mode === 'popover') setPopoverStyle(null);
      return;
    }
    let frameId;
    const updatePosition = () => {
      if (!popoverDimensionsRef.current) {
        const viewportWidth = window.innerWidth || 0;
        const viewportHeight = window.innerHeight || 0;
        const padding = 12;
        const maxHeight = Math.max(0, viewportHeight - padding * 2);
        popoverDimensionsRef.current = {
          width: Math.max(0, Math.min(POPOVER_MAX_WIDTH, viewportWidth - padding * 2)),
          height: Math.min(POPOVER_FIXED_HEIGHT, maxHeight || POPOVER_FIXED_HEIGHT),
        };
      }
      const nextStyle = buildPopoverLayout(pickerRef.current, popoverDimensionsRef.current);
      if (nextStyle) setPopoverStyle(nextStyle);
    };
    const schedule = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updatePosition);
    };
    schedule();
    window.addEventListener('resize', schedule);
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      window.removeEventListener('resize', schedule);
    };
  }, [open, mode]);

  useEffect(() => {
    if (!open || mode === 'inline') return;
    const handler = (event) => {
      if (!pickerRef.current) return;
      if (pickerRef.current.contains(event.target)) return;
      if (popoverRef.current && popoverRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, mode]);

  const dayMap = useMemo(() => {
    if (!Array.isArray(days)) return new Map();
    return new Map(days.map((day) => [day.date, day]));
  }, [days]);
  const minMonthStart = useMemo(() => {
    const base = parseIsoDate(minIso) || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  }, [minIso]);

  const leftMonth = useMemo(() => new Date(month.getFullYear(), month.getMonth(), 1), [month]);
  const rightMonth = useMemo(() => new Date(month.getFullYear(), month.getMonth() + 1, 1), [month]);
  const leftCells = useMemo(() => buildMonthCells(leftMonth), [leftMonth]);
  const rightCells = useMemo(() => buildMonthCells(rightMonth), [rightMonth]);

  const handleSelect = (iso, dayData) => {
    if (disabled) return;
    const unavailable = dayData && (dayData.available === false || dayData.status === 'blocked' || dayData.status === 'booked');
    if (iso < minIso || unavailable) return;
    setPanelError('');
    if (replaceExistingRangeRef.current) {
      replaceExistingRangeRef.current = false;
      selectionRef.current = { checkIn: iso, checkOut: '' };
      onChange(iso, '');
      return;
    }
    const currentCheckIn = selectionRef.current.checkIn;
    const currentCheckOut = selectionRef.current.checkOut;
    if (!currentCheckIn || (currentCheckIn && currentCheckOut)) {
      selectionRef.current = { checkIn: iso, checkOut: '' };
      onChange(iso, '');
      return;
    }
    if (iso <= currentCheckIn) {
      selectionRef.current = { checkIn: iso, checkOut: '' };
      onChange(iso, '');
      return;
    }
    const selectedNights = calcNights(currentCheckIn, iso);
    if (Number(minNights || 1) > 1 && selectedNights < Number(minNights || 1)) {
      setPanelError(`Minimum stay is ${Number(minNights || 1)} nights.`);
      return;
    }
    selectionRef.current = { checkIn: currentCheckIn, checkOut: iso };
    onChange(currentCheckIn, iso);
    if (closeOnComplete && mode === 'popover') {
      setOpen(false);
    }
  };

  const clearDates = () => {
    setPanelError('');
    selectionRef.current = { checkIn: '', checkOut: '' };
    onChange('', '');
  };

  const renderMonth = (labelDate, cells) => (
    <div className="flex flex-col gap-2">
      <div className="text-center text-sm font-semibold text-slate-800">{formatMonthLabel(labelDate)}</div>
      <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold text-slate-400">
        {WEEK_LABELS.map((day) => (
          <div key={day} className={day === 'Sat' || day === 'Sun' ? 'text-rose-500' : ''}>
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {cells.map((cell, idx) => {
          if (!cell) {
            return <div key={`empty-${labelDate.getMonth()}-${idx}`} className="h-9 w-9" />;
          }
          const dayData = dayMap.get(cell.iso);
          const unavailable = dayData && (dayData.available === false || dayData.status === 'blocked' || dayData.status === 'booked');
          const isPast = cell.iso < minIso;
          const isDisabled = disabled || isPast || unavailable;
          const isStart = checkIn && cell.iso === checkIn;
          const isEnd = checkOut && cell.iso === checkOut;
          const inRange = checkIn && checkOut && cell.iso > checkIn && cell.iso < checkOut;
          const isWeekend = cell.dateObj.getDay() === 0 || cell.dateObj.getDay() === 6;
          const baseStyle = 'h-9 w-9 rounded-full text-sm font-medium transition';
          const rangeStyle = inRange ? 'bg-rose-50 text-slate-900' : '';
          const selectedStyle = isStart || isEnd ? 'border border-rose-500 bg-white text-slate-900' : '';
          const disabledStyle = isDisabled ? 'text-slate-300 cursor-not-allowed' : 'hover:bg-rose-50';
          const weekendStyle = !isDisabled && !inRange && !isStart && !isEnd && isWeekend ? 'text-rose-500' : '';
          return (
            <button
              key={cell.iso}
              type="button"
              className={`${baseStyle} ${rangeStyle} ${selectedStyle} ${disabledStyle} ${weekendStyle}`}
              onClick={() => handleSelect(cell.iso, dayData)}
              disabled={isDisabled}
              title={dayData?.blackout || (unavailable ? 'Unavailable' : '')}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );

  const openPicker = () => {
    if (disabled) return;
    selectionRef.current = { checkIn, checkOut };
    replaceExistingRangeRef.current = Boolean(checkIn && checkOut);
    setOpen(true);
  };

  const popoverContent = (
    <div
      ref={popoverRef}
      className={`${mode === 'popover' ? '' : 'mt-2 w-full max-w-[90vw] md:min-w-[640px]'} rounded-2xl border border-slate-200 bg-white p-4 shadow-xl`}
      style={
        mode === 'popover'
          ? popoverStyle || { position: 'fixed', left: 12, top: 12, zIndex: 1200, visibility: 'hidden' }
          : undefined
      }
    >
      <div className="mb-3 flex items-center justify-between text-slate-700">
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 text-rose-500 hover:bg-rose-50"
          onClick={() => {
            const prev = new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1);
            if (prev < minMonthStart) return;
            setMonth(prev);
          }}
          disabled={loading}
        >
          <NavChevron direction="left" />
        </button>
        <div className="flex flex-1 items-center justify-center gap-6 text-sm font-semibold">
          <span>{formatMonthLabel(leftMonth)}</span>
          <span className="hidden md:inline">{formatMonthLabel(rightMonth)}</span>
        </div>
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 text-rose-500 hover:bg-rose-50"
          onClick={() => setMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1))}
          disabled={loading}
        >
          <NavChevron direction="right" />
        </button>
      </div>

      {error ? <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div> : null}
      {panelError ? <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">{panelError}</div> : null}
      {loading && <div className="mb-3 text-xs text-slate-500">Loading available dates...</div>}

      <div className="grid gap-6 md:grid-cols-2">
        {renderMonth(leftMonth, leftCells)}
        <div className="hidden md:block">{renderMonth(rightMonth, rightCells)}</div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <span>
          Pick check-in, then check-out.
          {Number(minNights || 1) > 1 ? ` Minimum stay ${Number(minNights || 1)} nights.` : ''}
        </span>
        <button type="button" className="text-rose-600 hover:underline" onClick={clearDates}>
          Clear dates
        </button>
      </div>
    </div>
  );

  return (
    <div ref={pickerRef} className={`relative ${className}`}>
      {showInputs && (
        <div className="flex w-full items-center overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <button
            type="button"
            className="flex flex-1 items-center justify-between gap-2 px-3 py-2 text-left text-sm text-slate-700"
            onClick={openPicker}
            disabled={disabled}
          >
            <span className={checkIn ? 'font-semibold text-slate-900' : 'text-slate-400'}>{checkIn ? formatDisplayDate(checkIn) : 'Check-In'}</span>
            <CalendarIcon className="h-4 w-4 text-rose-500" />
          </button>
          <div className="h-8 w-px bg-slate-200" />
          <button
            type="button"
            className="flex flex-1 items-center justify-between gap-2 px-3 py-2 text-left text-sm text-slate-700"
            onClick={openPicker}
            disabled={disabled}
          >
            <span className={checkOut ? 'font-semibold text-slate-900' : 'text-slate-400'}>{checkOut ? formatDisplayDate(checkOut) : 'Check-Out'}</span>
            <CalendarIcon className="h-4 w-4 text-rose-500" />
          </button>
        </div>
      )}

      {open &&
        (mode === 'popover' && typeof document !== 'undefined'
          ? createPortal(popoverContent, document.body)
          : popoverContent)}
    </div>
  );
};

export default memo(DateRangePicker);
